import { Container, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useResearchAssistantTabbedArea } from './useResearchAssistantTabbedArea';
import { DerivedEventEditCard } from '../../cohort-builder-edit-cards/DerivedEventEditCard/DerivedEventEditCard';
import IndexDateEditCard from '../../cohort-builder-edit-cards/IndexDateEditCard/IndexDateEditCard';
import { SimpleEventEditCard } from '../../cohort-builder-edit-cards/SimpleEventEditCard/SimpleEventEditCard';
import { StudyPeriodEditCard } from '../../cohort-builder-edit-cards/StudyPeriodEditCard/StudyPeriodEditCard';
import { SubjectVariableEditCard } from '../../cohort-builder-edit-cards/SubjectVariableEditCard/SubjectVariableEditCard';
import { CohortDefinitionSummary } from '../../cohort-builder-sections/CohortDefinitionSummary/CohortDefinitionSummary';

export const ResearchAssistantTabbedArea = ({
  isCohortVersionLoading,
}: {
  isCohortVersionLoading?: boolean;
}) => {
  const theme = useTheme();

  const {
    itemsToReviewCount,
    showDerivedEventEditModal,
    showIndexDate,
    showSimpleEventEditModal,
    showSubjectVariableEditModal,
    showStudyPeriodEditModal,
  } = useResearchAssistantTabbedArea();

  // TODO: propagate fieldErrors to parent component for error icons
  //const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  return (
    <Container
      sx={{
        display: 'flex',
        flex: 2,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        width: '100%',
        overflow: 'auto',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        marginRight: theme.spacing(6),
        '@media (min-width: 1200px)': {
          maxWidth: 'none',
        },
        '&.MuiContainer-root': {
          '@media (min-width: 600px)': {
            padding: '0',
          },
        },
      }}
    >
      {/* Header */}
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          height: 73, // match the height of the Research Assistant chat header
          padding: `${theme.spacing(4)} ${theme.spacing(6)} !important`,
        }}
      >
        <Typography variant="h6">Review cohort definition</Typography>
        <Typography variant="subtitle1" color={itemsToReviewCount ? 'textPrimary' : 'textDisabled'}>
          {itemsToReviewCount ? (
            <FormattedMessage
              id="projects.cohortBuilderPage.researchAssistant.itemsToReview"
              values={{ itemsToReviewCount }}
            />
          ) : (
            <FormattedMessage id="projects.cohortBuilderPage.researchAssistant.noItemsToReview" />
          )}
        </Typography>
      </Container>

      {/* Content - cohort summary */}
      {isCohortVersionLoading ? (
        <Stack
          spacing={2}
          sx={{
            padding: theme.spacing(2),
            width: '100%',
            height: '100%',
            minHeight: '400px',
          }}
        >
          <Skeleton variant="rectangular" height={45} />
          <Skeleton variant="rectangular" height={45} />
          <Skeleton variant="rectangular" height={45} />
          <Skeleton variant="rectangular" height={45} />
          <Skeleton variant="rectangular" height={45} />
          <Skeleton variant="rectangular" height={45} />
        </Stack>
      ) : (
        <Container
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
            padding: `${theme.spacing(6)} !important`,
          }}
        >
          <CohortDefinitionSummary />
          {/* TODO: Add ability to manually modify cohorts */}
          {/* <CohortActionsSpeedDial hasIndexDate={!!cohortDefinition?.indexDate} /> */}
        </Container>
      )}

      {/* modals  */}
      {showSimpleEventEditModal && <SimpleEventEditCard />}
      {showDerivedEventEditModal && <DerivedEventEditCard />}
      {showIndexDate && <IndexDateEditCard />}
      {showSubjectVariableEditModal && <SubjectVariableEditCard />}
      {showStudyPeriodEditModal && <StudyPeriodEditCard />}
    </Container>
  );
};
